import { useState, useEffect, useMemo } from "react";
import { useLocation, useRoute } from "wouter";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Loader2, AlertTriangle, CheckCircle2 } from "lucide-react";

// Calcula cor de texto legível sobre um fundo
function getContrastColor(hexColor: string): string {
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#002F59' : '#FFFFFF';
}

export default function ResetPassword() {
  const [, setLocation] = useLocation();
  const [, params] = useRoute("/reset-password");
  
  // Extrair token e email da query string (estável)
  const searchParams = useMemo(() => new URLSearchParams(window.location.search), []);
  const token = useMemo(() => searchParams.get("token") || "", [searchParams]);
  const email = useMemo(() => searchParams.get("email") || "", [searchParams]);

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isValid, setIsValid] = useState(false);
  const [success, setSuccess] = useState(false);

  // Buscar branding do canal do usuário
  const { data: branding } = trpc.auth.getUserBranding.useQuery(
    { email },
    { enabled: !!email, retry: false, staleTime: Infinity }
  );

  // Cores resolvidas (com fallback Salvia)
  const primaryColor = branding?.primaryColor || '#002F59';
  const accentColor = branding?.accentColor || '#C0FF00';
  const logoUrl = branding?.logoUrl || null;
  const productName = branding?.productName || 'Insight NR1';
  const accentTextColor = getContrastColor(accentColor);
  const primaryTextColor = getContrastColor(primaryColor);

  const resetPasswordMutation = trpc.auth.resetPasswordPublic.useMutation({
    onSuccess: (data) => {
      setSuccess(true);
      toast.success(data.message);
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  // Validar formulário
  useEffect(() => {
    const isPasswordValid = password.length >= 8;
    const doPasswordsMatch = password === confirmPassword && password.length > 0;
    setIsValid(isPasswordValid && doPasswordsMatch && token.length === 64 && email.length > 0);
  }, [password, confirmPassword, token, email]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isValid) {
      toast.error("Preencha todos os campos corretamente");
      return;
    }

    resetPasswordMutation.mutate({
      token,
      email,
      password,
    });
  };

  // Componente de logo/ícone dinâmico
  const BrandLogo = () => {
    if (logoUrl) {
      return (
        <div className="mx-auto mb-4">
          <img src={logoUrl} alt={productName} className="max-h-16 max-w-[200px] object-contain mx-auto" />
        </div>
      );
    }
    return (
      <div
        className="mx-auto mb-4 w-16 h-16 rounded-full flex items-center justify-center"
        style={{ backgroundColor: accentColor }}
      >
        <span className="text-2xl font-bold" style={{ color: accentTextColor }}>
          {productName.substring(0, 2).toUpperCase()}
        </span>
      </div>
    );
  };

  // Tela de sucesso
  if (success) {
    return (
      <div
        className="min-h-screen flex items-center justify-center p-4"
        style={{ backgroundColor: primaryColor }}
      >
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
            <CardTitle className="text-2xl">Senha Redefinida com Sucesso!</CardTitle>
            <CardDescription>
              Sua nova senha foi definida. Agora você pode fazer login no sistema.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              onClick={() => setLocation("/")}
              className="w-full text-white"
              style={{ backgroundColor: primaryColor }}
            >
              Ir para o Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Verificar se token e email estão presentes
  if (!token || !email) {
    return (
      <div
        className="min-h-screen flex items-center justify-center p-4"
        style={{ backgroundColor: primaryColor }}
      >
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
            <CardTitle className="text-2xl">Link Inválido</CardTitle>
            <CardDescription>
              O link de redefinição de senha está incompleto ou inválido. 
              Solicite ao administrador o reenvio do link de reset.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              onClick={() => setLocation("/")}
              className="w-full"
            >
              Voltar para o Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{ backgroundColor: primaryColor }}
    >
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <BrandLogo />
          <CardTitle className="text-2xl">Redefinir Senha</CardTitle>
          <CardDescription>
            Crie uma nova senha segura para acessar o sistema {productName}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email (readonly) */}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                readOnly
                className="bg-gray-50"
              />
            </div>

            {/* Nova Senha */}
            <div className="space-y-2">
              <Label htmlFor="password">Nova Senha</Label>
              <Input
                id="password"
                type="password"
                placeholder="Mínimo 8 caracteres"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              {password.length > 0 && password.length < 8 && (
                <p className="text-sm text-red-600">
                  Senha deve ter no mínimo 8 caracteres
                </p>
              )}
            </div>

            {/* Confirmar Nova Senha */}
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirmar Nova Senha</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="Digite a senha novamente"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
              {confirmPassword.length > 0 && password !== confirmPassword && (
                <p className="text-sm text-red-600">
                  As senhas não coincidem
                </p>
              )}
            </div>

            {/* Erro do backend */}
            {resetPasswordMutation.isError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-700">
                  {resetPasswordMutation.error?.message || "Erro ao redefinir senha. O link pode ter expirado ou já ter sido utilizado. Solicite ao administrador o reenvio do link."}
                </p>
              </div>
            )}

            {/* Botão Redefinir Senha */}
            <Button
              type="submit"
              className="w-full"
              style={{ backgroundColor: primaryColor, color: primaryTextColor }}
              disabled={!isValid || resetPasswordMutation.isPending}
            >
              {resetPasswordMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Redefinindo senha...
                </>
              ) : (
                "Redefinir Senha"
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
